import { useCallback, useEffect, useRef } from 'react';

const DRAFT_KEY_PREFIX = 'proposal_chat_draft_';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DRAFT_MAX_COUNT = 10;
const DEBOUNCE_MS = 500;

interface DraftEntry {
  message: string;
  timestamp: number;
  proposalId: number;
}

/**
 * Persists chat drafts to localStorage with automatic cleanup.
 *
 * Features:
 * - Debounced saves (500ms) to avoid excessive localStorage writes
 * - Automatic expiry after 7 days
 * - Limits total drafts to 10 (per-org cleanup on mount)
 * - Graceful handling of corrupted localStorage entries
 *
 * @param proposalId - The proposal ID to key the draft
 * @param message - Current message value from parent state
 * @param setMessage - Setter to restore draft on mount
 */
export function useChatDraft(
  proposalId: number,
  message: string,
  setMessage: (msg: string) => void
) {
  const draftKey = `${DRAFT_KEY_PREFIX}${proposalId}`;
  // Track if we've loaded the initial draft to avoid overwriting it with empty message
  const hasLoaded = useRef(false);

  // Load draft on mount (only if < 7 days old)
  useEffect(() => {
    const raw = localStorage.getItem(draftKey);
    if (raw) {
      try {
        const entry: DraftEntry = JSON.parse(raw);
        if (Date.now() - entry.timestamp < DRAFT_MAX_AGE_MS) {
          setMessage(entry.message);
        } else {
          localStorage.removeItem(draftKey); // Expired
        }
      } catch {
        localStorage.removeItem(draftKey); // Corrupted
      }
    }
    hasLoaded.current = true;
    // Cleanup old drafts on mount
    cleanupOldDrafts();
    // Only run on mount - proposalId changes handled by parent remounting
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  // Save draft on change (debounced 500ms)
  useEffect(() => {
    // Don't save until we've loaded the initial draft
    if (!hasLoaded.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (message.trim()) {
        const entry: DraftEntry = {
          message,
          timestamp: Date.now(),
          proposalId,
        };
        localStorage.setItem(draftKey, JSON.stringify(entry));
      } else {
        localStorage.removeItem(draftKey);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [message, proposalId, draftKey]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(draftKey);
  }, [draftKey]);

  // Check if draft exists (for UI indicators)
  const hasDraft = useCallback(() => {
    return !!localStorage.getItem(draftKey);
  }, [draftKey]);

  return { hasDraft, clearDraft };
}

/**
 * Cleanup: remove drafts older than 7 days, keep max 10 most recent
 * Uses Storage API (.key() and .length) for proper iteration.
 */
function cleanupOldDrafts() {
  // Collect all draft keys using Storage API
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(DRAFT_KEY_PREFIX)) {
      keys.push(key);
    }
  }

  const entries: { key: string; timestamp: number }[] = [];

  for (const key of keys) {
    try {
      const entry = JSON.parse(localStorage.getItem(key) || '');
      if (typeof entry?.timestamp !== 'number') {
        localStorage.removeItem(key); // Malformed
        continue;
      }
      if (Date.now() - entry.timestamp > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(key); // Expired
      } else {
        entries.push({ key, timestamp: entry.timestamp });
      }
    } catch {
      localStorage.removeItem(key); // Corrupted
    }
  }

  // Keep only 10 most recent
  if (entries.length > DRAFT_MAX_COUNT) {
    entries.sort((a, b) => b.timestamp - a.timestamp);
    for (const { key } of entries.slice(DRAFT_MAX_COUNT)) {
      localStorage.removeItem(key);
    }
  }
}

// Export constants for testing
export const DRAFT_CONSTANTS = {
  DRAFT_KEY_PREFIX,
  DRAFT_MAX_AGE_MS,
  DRAFT_MAX_COUNT,
  DEBOUNCE_MS,
};
